<script setup lang="ts">
import type { SimpleListElementProps } from "./SimpleListElementProps";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";
import ScrollAnimation from "@/views/blocks/shared/section/animations/ScrollAnimation.vue";
import { computed } from "vue";
import HorizontalSpacing from "@/views/blocks/shared/section/layout/spacing/HorizontalSpacing.vue";

const props = defineProps<{
    block: PageBlock<SimpleListElementProps>;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const imageSize = computed(() => {
    const textSize = dataValues.value.formatting.data.textSize;
    const sizeMap: Record<string, { class: string; width: number; height: number }> = {
        "text-sm": { class: "size-4", width: 16, height: 16 },
        "text-base": { class: "size-5", width: 20, height: 20 },
        "text-lg": { class: "size-6", width: 24, height: 24 },
        "text-xl": { class: "size-7", width: 28, height: 28 },
        "text-2xl": { class: "size-8", width: 32, height: 32 },
        "text-3xl": { class: "size-9", width: 36, height: 36 },
    };
    return sizeMap[textSize] || sizeMap["text-base"];
});
</script>

<template>
    <ScrollAnimation :type="scrollAnimationType">
        <div
            :id="block.webId"
            :style="{
                backgroundColor: styleValues.background,
            }"
        >
            <HorizontalSpacing
                :class="[
                    dataValues.formatting.data.paddingTop,
                    dataValues.formatting.data.paddingBottom,
                ]"
            >
                <div class="mx-auto max-w-2xl lg:max-w-7xl">
                    <div
                        :class="[
                            dataValues.formatting.data.elementAlign,
                            dataValues.formatting.data.maxWidth,
                        ]"
                    >
                        <ul role="list" class="flex flex-col gap-y-4 text-balance">
                            <EditableElement
                                v-for="item in dataValues.items.data.items"
                                :key="item.id"
                                :blockId="block.id"
                                :fieldName="`items_${item.id}`"
                                :hideable="false"
                            >
                                <li class="flex gap-x-3">
                                    <Image
                                        :src="item.iconSrc"
                                        alt=""
                                        :class="['mt-[2px] flex-none', imageSize.class]"
                                        :width="imageSize.width"
                                        :height="imageSize.height"
                                        aria-hidden="true"
                                    />
                                    <span :class="dataValues.formatting.data.textSize">
                                        <strong :style="{ color: styleValues.text }">{{
                                            item.title
                                        }}</strong>
                                        {{ " " }}
                                        <span
                                            class="opacity-70"
                                            :style="{ color: styleValues.text }"
                                            >{{ item.description }}</span
                                        >
                                    </span>
                                </li>
                            </EditableElement>
                        </ul>
                    </div>
                </div>
            </HorizontalSpacing>
        </div>
    </ScrollAnimation>
</template>
